import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, Button, FormControlLabel, Switch } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';

/**
 * Notification Preferences component
 * Form for managing notification settings
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data
 * @param {Function} props.handleInputChange - Function to handle input changes
 * @param {Function} props.handleSubmit - Function to handle form submission
 * @param {boolean} props.saving - Whether the form is currently saving
 * @returns {JSX.Element} The NotificationPreferences component
 */
const NotificationPreferences = ({ formData, handleInputChange, handleSubmit, saving }) => {
  const { t } = useTranslation('user');

  return (
    <form onSubmit={handleSubmit}>
      <Grid
        container
        spacing={3}
      >
        <Grid
          item
          xs={12}
        >
          <Typography
            variant="h6"
            gutterBottom
          >
            {t('forms.notificationPreferences')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {t('forms.notificationDescription')}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formData.emailNotifications}
                onChange={handleInputChange}
                name="emailNotifications"
                color="primary"
              />
            }
            label={t('forms.emailNotifications')}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 4 }}
          >
            {t('forms.emailNotificationsDescription')}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formData.orderUpdates}
                onChange={handleInputChange}
                name="orderUpdates"
                color="primary"
                disabled={!formData.emailNotifications}
              />
            }
            label={t('forms.orderUpdates')}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 4 }}
          >
            {t('forms.orderUpdatesDescription')}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formData.promotionalEmails}
                onChange={handleInputChange}
                name="promotionalEmails"
                color="primary"
                disabled={!formData.emailNotifications}
              />
            }
            label={t('forms.promotionalEmails')}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 4 }}
          >
            {t('forms.promotionalEmailsDescription')}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<NotificationsIcon />}
            disabled={saving}
          >
            {saving ? t('forms.saving') : t('forms.savePreferences')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default NotificationPreferences;
